/**
 * ESC/POS thermal receipt for the MOBILE POS via RawBT (Android bridge app).
 *
 * WHY THIS EXISTS: a mobile browser can't talk to a Bluetooth-Classic thermal
 * printer directly. RawBT (already installed/paired on the device) is the bridge:
 * we build the raw ESC/POS byte stream here and hand it to RawBT through its
 * `rawbt:base64,...` URL. RawBT prints it on the 58mm printer.
 *
 * Scoped PER DEVICE (localStorage flag) so only the phone the cashier flips ON
 * routes to RawBT — desktop tills keep the normal browser print untouched.
 *
 * The receipt build is SYNCHRONOUS: the company logo is rasterised once and
 * cached, so at PAY the send happens inside the tap gesture (custom-scheme app
 * launches need an active user gesture; an await would break it and the browser
 * would block RawBT).
 */

import { formatCurrency, formatDate } from './formatters';
import { formatTwoUnitQty } from './unitConversion';
import { isPrintBrandHidden, printBrandThermalLines } from './printBranding';

const RAWBT_FLAG = 'amal_pos_rawbt';   // per-device on/off

// Paper profiles: characters per line (Font A) + printable dots for the logo.
const PROFILES = {
  '58mm': { chars: 32, dots: 384 },
  '80mm': { chars: 48, dots: 576 },
};

// ── Device flag (localStorage) ──────────────────────────────────────────────
export const isRawBTEnabled = () => {
  try { return localStorage.getItem(RAWBT_FLAG) === '1'; } catch { return false; }
};
export const setRawBTEnabled = (on) => {
  try { localStorage.setItem(RAWBT_FLAG, on ? '1' : '0'); } catch { /* ignore */ }
};

// ── Small currency formatter mirroring POSReceipt's fmt() ───────────────────
const fmtMoney = (cfg, v) => {
  const n = formatCurrency(v || 0);
  if (cfg.currencyPosition === 'after') {
    const m = String(n).match(/^([^0-9\-]*)([\s\S]*)$/);
    if (m && m[1].trim()) return `${m[2].trim()} ${m[1].trim()}`;
  }
  return n;
};

// ── ESC/POS byte builder ────────────────────────────────────────────────────
class Esc {
  constructor() { this.buf = []; }
  raw(...bytes) { for (const b of bytes) this.buf.push(b & 0xFF); return this; }
  // Encode to single-byte (Latin-1-ish); anything outside falls back to '?'.
  text(s) {
    const str = String(s == null ? '' : s);
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      this.buf.push(c <= 0xFF ? c : 0x3F);
    }
    return this;
  }
  line(s = '') { return this.text(s).raw(0x0A); }
  init() { return this.raw(0x1B, 0x40); }          // ESC @
  align(n) { return this.raw(0x1B, 0x61, n); }      // 0 left, 1 center, 2 right
  bold(on) { return this.raw(0x1B, 0x45, on ? 1 : 0); }
  size(double) { return this.raw(0x1D, 0x21, double ? 0x11 : 0x00); } // GS ! (double both / normal)
  feed(n) { return this.raw(0x1B, 0x64, n); }       // ESC d n
  bytes() { return Uint8Array.from(this.buf); }
}

const repeat = (ch, n) => (n > 0 ? ch.repeat(n) : '');
const divider = (chars) => repeat('-', chars);

// Left text + right text padded to `chars`. Right value is kept whole; the left
// is trimmed if the two would collide.
const cols = (left, right, chars) => {
  left = String(left || ''); right = String(right || '');
  const space = chars - right.length;
  if (space <= 1) return (left + ' ' + right).slice(0, chars);
  if (left.length > space - 1) left = left.slice(0, space - 1);
  return left + repeat(' ', chars - left.length - right.length) + right;
};

const centre = (s, chars) => {
  s = String(s || '');
  if (s.length >= chars) return s.slice(0, chars);
  const pad = Math.floor((chars - s.length) / 2);
  return repeat(' ', pad) + s;
};

// Word-wrap a long line to width.
const wrap = (s, chars) => {
  const out = []; let cur = '';
  for (const w of String(s || '').split(/\s+/)) {
    if (!w) continue;
    if ((cur + ' ' + w).trim().length > chars) { if (cur) out.push(cur); cur = w.length > chars ? w.slice(0, chars) : w; }
    else cur = (cur ? cur + ' ' : '') + w;
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
};

// ── Logo raster cache (async prep, sync use) ────────────────────────────────
const logoCache = {};   // url|dots -> { widthBytes, height, data:Uint8Array } | 'FAILED'

export const preloadLogoRaster = (url, dots) => {
  const key = `${url}|${dots}`;
  if (!url || logoCache[key]) return Promise.resolve(logoCache[key] || null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';   // needs Firebase Storage CORS; on failure we skip the logo
    img.onload = () => {
      try {
        const maxH = 240;
        let w = Math.min(dots, img.naturalWidth || dots);
        let h = Math.round((img.naturalHeight / img.naturalWidth) * w) || 0;
        if (h > maxH) { const s = maxH / h; h = maxH; w = Math.round(w * s); }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const px = ctx.getImageData(0, 0, w, h).data;
        const widthBytes = Math.ceil(w / 8);
        const data = new Uint8Array(widthBytes * h);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
            const alpha = px[i + 3];
            if (alpha > 40 && lum < 140) data[y * widthBytes + (x >> 3)] |= (0x80 >> (x & 7));
          }
        }
        logoCache[key] = { widthBytes, height: h, data };
      } catch { logoCache[key] = 'FAILED'; }
      resolve(logoCache[key] === 'FAILED' ? null : logoCache[key]);
    };
    img.onerror = () => { logoCache[key] = 'FAILED'; resolve(null); };
    img.src = url;
  });
};

const rasterCmd = (e, raster) => {
  if (!raster || raster === 'FAILED') return;
  const { widthBytes, height, data } = raster;
  e.align(1);
  e.raw(0x1D, 0x76, 0x30, 0x00,
    widthBytes & 0xFF, (widthBytes >> 8) & 0xFF,
    height & 0xFF, (height >> 8) & 0xFF);
  for (let i = 0; i < data.length; i++) e.raw(data[i]);
  e.raw(0x0A);
};

// ── Build the full receipt bytes (synchronous) ──────────────────────────────
const buildOne = (e, data, company, cfg, chars, raster) => {
  const {
    docNumber, docDate, lines = [], cart = [],
    subtotal, discountAmount, taxAmount, totalAmount,
    paymentMethod, payments, cashTendered, changeGiven,
    customerName, cashierName, previousBalance, exchange,
  } = data;
  const rows = cart.length > 0 ? cart : lines;
  const dateStr = formatDate(docDate || new Date().toISOString().split('T')[0]);
  const timeStr = new Date().toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });

  // Header
  if (cfg.showLogo && raster) rasterCmd(e, raster);
  e.align(1);
  if (cfg.showCompanyName) { e.bold(true).size(true).line(company?.companyName || 'Store').size(false).bold(false); }
  if (cfg.showAddress && company?.address) wrap(company.address, chars).forEach(l => e.line(l));
  const contact = [];
  if (cfg.showPhone && company?.phone) contact.push('Tel: ' + company.phone);
  if (cfg.showNTN && company?.ntn) {
    const lbl = (company?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN';
    contact.push(`${lbl}: ${company.ntn}`);
  }
  contact.forEach(c => wrap(c, chars).forEach(l => e.line(l)));
  if (cfg.headerText) wrap(cfg.headerText, chars).forEach(l => e.line(l));

  e.align(0);
  e.line(divider(chars));
  e.line(cols('Receipt#', docNumber || '', chars));
  e.line(cols('Date', `${dateStr} ${timeStr}`, chars));
  if (cfg.showCashier && cashierName) e.line(cols('Cashier', cashierName, chars));
  if (cfg.showCustomer && customerName && customerName !== 'Walk-in Customer') e.line(cols('Customer', customerName, chars));
  e.line(divider(chars));

  // Items
  rows.forEach(it => {
    const name = it.itemName || it.description || '';
    const code = cfg.showItemCode && it.itemCode ? ` [${it.itemCode}]` : '';
    wrap(name + code, chars).forEach(l => e.line(l));
    // Show Two Units: both units ("14.4 Mtr x 100" then "= 10 Ctn").
    const two = it.secondUnitCode && it.secondQuantity != null;
    const qty = two ? formatTwoUnitQty(it.quantity || 0) : (it.quantity || 0);
    const uom = two ? (it.unitCode || it.unitOfMeasure || '') : (cfg.showUoM ? (it.unitOfMeasure || '') : '');
    const rate = it.unitPrice || it.priceExclTax || 0;
    const amount = it.lineTotal || ((it.quantity || 0) * rate);
    e.line(cols(`  ${qty}${uom ? ' ' + uom : ''} x ${fmtMoney(cfg, rate)}`, fmtMoney(cfg, amount), chars));
    if (two) e.line(`  = ${formatTwoUnitQty(it.secondQuantity)} ${it.secondUnitCode}`);
    const dP = Number(it.discountPercent) || 0, dA = Number(it.discountAmount) || 0;
    if (cfg.showDiscountColumn && (dP > 0 || dA > 0)) e.line(`  Disc ${dP > 0 ? dP + '%' : fmtMoney(cfg, dA)}`);
  });
  e.line(divider(chars));

  // Totals
  if (cfg.showSubtotal) e.line(cols('Subtotal', fmtMoney(cfg, subtotal || 0), chars));
  const bankDisc = Number(data.bankDiscount?.amount) || 0;
  if (bankDisc > 0) {
    // POS bank card offer on its own line; "Discount" keeps only the rest.
    const otherDisc = Math.round(((Number(discountAmount) || 0) - bankDisc) * 100) / 100;
    if (otherDisc > 0.004) e.line(cols('Discount', '-' + fmtMoney(cfg, otherDisc), chars));
    e.line(cols(`${data.bankDiscount.label || 'Bank'} ${data.bankDiscount.percent ?? ''}%`.slice(0, Math.max(8, chars - 12)), '-' + fmtMoney(cfg, bankDisc), chars));
    if (data.bankDiscount.cardMask) e.line(cols('Card', String(data.bankDiscount.cardMask), chars));
  } else if (discountAmount > 0) e.line(cols('Discount', '-' + fmtMoney(cfg, discountAmount), chars));
  if (cfg.showTaxBreakdown && taxAmount > 0) e.line(cols('Tax', fmtMoney(cfg, taxAmount), chars));

  // Exchange
  if (exchange && exchange.isExchange) {
    e.line(divider(chars));
    e.line(`EXCHANGE vs ${exchange.originalDocNumber || ''}`);
    (exchange.returnLines || []).forEach(it => {
      const qty = it.quantity || 0, rate = it.unitPrice || 0;
      wrap((it.itemName || '') + ' RETURN', chars).forEach(l => e.line(l));
      e.line(cols(`  ${qty} x ${fmtMoney(cfg, rate)}`, '-' + fmtMoney(cfg, it.lineTotal || qty * rate), chars));
    });
    e.line(cols('Return credit', '-' + fmtMoney(cfg, exchange.returnSubtotal || 0), chars));
    const net = Number(exchange.netAmount) || 0;
    e.bold(true).line(cols(net > 0.01 ? 'NET DUE' : net < -0.01 ? 'REFUND DUE' : 'EVEN', fmtMoney(cfg, Math.abs(net)), chars)).bold(false);
  }

  // Grand total (bold + double)
  e.align(0).bold(true).size(true);
  e.line(cols('TOTAL', fmtMoney(cfg, totalAmount || 0), Math.ceil(chars / 2)));
  e.size(false).bold(false);

  // Payment
  if (cfg.showPaymentMethod) {
    e.line(divider(chars));
    if (payments && payments.length > 0) {
      payments.filter(p => p.amount > 0).forEach(p => e.line(cols((p.method || '').toUpperCase(), fmtMoney(cfg, p.amount), chars)));
    } else if (totalAmount > 0) {
      e.line(cols((paymentMethod || 'CASH').toUpperCase(), fmtMoney(cfg, totalAmount), chars));
    }
    if (cfg.showTenderedChange && cashTendered > 0) {
      e.line(cols('Tendered', fmtMoney(cfg, cashTendered), chars));
      e.line(cols('Change', fmtMoney(cfg, changeGiven || 0), chars));
    }
  }

  // Account balance (named credit customers)
  const prev = Number(previousBalance);
  if (cfg.showPreviousBalance && previousBalance != null && !Number.isNaN(prev) && customerName && customerName !== 'Walk-in Customer') {
    const bill = Number(totalAmount) || 0;
    const paid = Math.min((Array.isArray(payments) ? payments : [])
      .filter(p => String(p.method || '').toLowerCase() !== 'credit')
      .reduce((s, p) => s + (Number(p.amount) || 0), 0), bill);
    e.line(divider(chars));
    e.line(cols('Prev Balance', fmtMoney(cfg, prev), chars));
    e.line(cols('This Bill', fmtMoney(cfg, bill), chars));
    e.line(cols('Paid', '-' + fmtMoney(cfg, paid), chars));
    e.bold(true).line(cols('New Balance', fmtMoney(cfg, prev + bill - paid), chars)).bold(false);
  }

  // Footer
  e.align(1);
  [cfg.promoMessage, cfg.footerText, cfg.returnPolicyText].forEach(t => {
    if (t) { e.line(''); wrap(t, chars).forEach(l => e.line(l)); }
  });
  if (!isPrintBrandHidden(company)) { e.line(''); printBrandThermalLines(chars).forEach(l => e.line(l)); }
  e.feed(4);
};

export const buildReceiptBytes = (data, company, cfg) => {
  const prof = PROFILES[cfg.paperSize] || PROFILES['58mm'];
  const logoUrl = cfg.showLogo ? (company?.logoUrl || company?.brandLogoUrl) : null;
  const raster = logoUrl ? (logoCache[`${logoUrl}|${prof.dots}`] || null) : null;
  const e = new Esc();
  e.init();
  const copies = Math.max(1, Math.min(5, Number(cfg.copies) || 1));
  for (let i = 0; i < copies; i++) buildOne(e, data, company, cfg, prof.chars, raster && raster !== 'FAILED' ? raster : null);
  return e.bytes();
};

// ── Send to RawBT via its URL scheme ────────────────────────────────────────
const bytesToBase64 = (bytes) => {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
};

export const sendBytesToRawBT = (bytes) => {
  const url = 'rawbt:base64,' + bytesToBase64(bytes);
  const a = document.createElement('a');
  a.href = url;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { try { document.body.removeChild(a); } catch { /* noop */ } }, 500);
};

/**
 * Synchronous print entry used by POSReceipt.printReceipt when the device flag
 * is on. Kicks off logo caching for NEXT time if the logo isn't ready yet, but
 * always sends now (so the rawbt: launch stays inside the tap gesture).
 */
export const printViaRawBT = (data, company, cfg) => {
  const prof = PROFILES[cfg.paperSize] || PROFILES['58mm'];
  const logoUrl = cfg.showLogo ? (company?.logoUrl || company?.brandLogoUrl) : null;
  if (logoUrl && !logoCache[`${logoUrl}|${prof.dots}`]) {
    preloadLogoRaster(logoUrl, prof.dots);   // ready from the next receipt
  }
  sendBytesToRawBT(buildReceiptBytes(data, company, cfg));
};

// ── Shift Z-Report (same paper width as the receipt) ────────────────────────
const dtStr = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-PK', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return String(iso); }
};

export const buildZReportBytes = (shift, company, cfg) => {
  const prof = PROFILES[cfg?.paperSize] || PROFILES['58mm'];
  const chars = prof.chars;
  const e = new Esc();
  e.init();
  e.align(1).bold(true).size(true).line(company?.companyName || company?.name || 'AMAL ERP').size(false);
  e.line('SHIFT Z-REPORT').bold(false);
  e.line(shift.shiftNumber || '');
  e.align(0).line(divider(chars));

  e.line(cols('Counter', `${shift.counterName || ''} ${shift.counterCode ? '(' + shift.counterCode + ')' : ''}`.trim(), chars));
  if (shift.branchName) e.line(cols('Branch', shift.branchName, chars));
  e.line(cols('Opened by', shift.openedByName || '-', chars));
  e.line(cols('Opened at', dtStr(shift.openedAt), chars));
  e.line(cols('Closed by', shift.closedByName || '-', chars));
  e.line(cols('Closed at', dtStr(shift.closedAt), chars));

  e.line('').bold(true).line('SALES').bold(false);
  e.line(cols(`Sales (${shift.saleCount || 0})`, fmtMoney(cfg, shift.grossSales), chars));
  e.line(cols(`Returns (${shift.returnCount || 0})`, '-' + fmtMoney(cfg, shift.grossReturns), chars));

  e.line('').bold(true).line('BY PAYMENT').bold(false);
  e.line(cols('Cash Sales', fmtMoney(cfg, shift.systemCashSales), chars));
  e.line(cols('Cash Refunds', '-' + fmtMoney(cfg, shift.systemCashReturns), chars));
  e.line(cols('Card / Bank', fmtMoney(cfg, shift.systemCardSales), chars));
  e.line(cols('Credit Sales', fmtMoney(cfg, shift.systemCreditSales), chars));

  e.line('').bold(true).line('CASH DRAWER').bold(false);
  e.line(cols('Opening Cash', fmtMoney(cfg, shift.openingCash), chars));
  e.line(cols('Expected Cash', fmtMoney(cfg, shift.expectedCash), chars));
  e.line(cols('Counted Cash', fmtMoney(cfg, shift.countedCash), chars));
  e.line(divider(chars));
  e.bold(true).size(true).line(cols('Variance', fmtMoney(cfg, Number(shift.variance) || 0), Math.ceil(chars / 2))).size(false).bold(false);

  if (shift.notes) { e.line(divider(chars)); wrap('Notes: ' + shift.notes, chars).forEach(l => e.line(l)); }
  e.align(1).line(divider(chars)).line('Printed ' + dtStr(new Date().toISOString()));
  if (!isPrintBrandHidden(company)) printBrandThermalLines(chars).forEach(l => e.line(l));
  e.feed(4);
  return e.bytes();
};

export const printZReportViaRawBT = (shift, company, cfg) => {
  sendBytesToRawBT(buildZReportBytes(shift, company, cfg || {}));
};
